import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
// import { useAuth } from "@/hooks/useAuth";
import { CartDrawer } from "@/components/business/cart/CartDrawer";
import NavigationHeader from "@/components/ui/navigation-header";
import  { BreadcrumbItem } from "@/components/ui/breadcrumbs";
import { User } from "lucide-react";

/**
 * Props del componente DashboardLayout
 */
interface DashboardLayoutProps {
  children?: React.ReactNode;
}

/**
 * Layout para las páginas del dashboard del apoderado
 *
 * Incluye un header unificado con información del usuario y navegación,
 * ThemeToggle para cambiar tema, breadcrumbs dinámicos, y renderiza el contenido.
 */
const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  // const { user } = useAuth();
  const location = useLocation();
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Generar breadcrumbs dinámicamente basado en la ruta
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0) return [];

    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Mapear segmentos a nombres legibles
      const segmentMap: Record<string, string> = {
        dashboard: "Dashboard",
        "student-dashboard": "Dashboard del Estudiante",
        profile: "Mi Perfil",
        children: "Gestión de Hijos",
        products: "Productos",
        orders: "Pedidos",
        reports: "Reportes",
        settings: "Configuración",
      };

      const label =
        segmentMap[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1);

      breadcrumbs.push({
        label,
        href: index === pathSegments.length - 1 ? undefined : currentPath,
        current: index === pathSegments.length - 1,
      });
    });

    return breadcrumbs;
  };

  // Logo personalizado para el dashboard
  const dashboardLogo = (
    <div className="flex items-center space-x-2">
      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-sm">
        <User className="h-6 w-6 text-primary-foreground" />
      </div>
      <div>
        <h1 className="text-lg md:text-xl font-bold text-card-foreground">
          Rincón Chilenito
        </h1>
        <p className="text-xs text-muted-foreground hidden sm:block text-left">
          Panel del Apoderado
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header Unificado */}
      <NavigationHeader
        variant="dashboard"
        title="Rincón Chilenito"
        subtitle="Panel del Apoderado"
        logo={dashboardLogo}
        breadcrumbs={getBreadcrumbs()}
        showCart={true}
        showThemeToggle={true}
        showUserMenu={true}
        userMenuVariant="compact"
        onCartClick={() => setIsCartOpen(true)}
      />

      {/* Contenido principal */}
      <main className="container mx-auto px-4 py-6">
        {children || <Outlet />}
      </main>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default DashboardLayout;
